import React, { useState, useMemo } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import AdminDownbar from '../../components/ui/AdminDownbar';
import Icon from '../../components/AppIcon';
import useAuthors from '../../hooks/useAuthors';
import Input from '../../components/ui/Input';

const AdminAuthorsScreen = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Usamos el hook dedicado que maneja toda la lógica de los autores.
  const {
    authors,
    loading,
    error,
    clearError,
    addAuthor,
    updateAuthor,
    deleteAuthor,
  } = useAuthors();

  // Ordenamos y filtramos los autores por ID y búsqueda.
  const filteredAuthors = useMemo(() => {
    if (!Array.isArray(authors)) return [];
    
    let filtered = [...authors];
    
    // Aplicar filtro de búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(author => 
        author.name_author.toLowerCase().includes(query)
      );
    }
    
    return filtered.sort((a, b) => a.id_author - b.id_author);
  }, [authors, searchQuery]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;

    const name = form.name.value.trim();

    if (!name) {
      alert('Por favor, introduce el nombre del autor.');
      return;
    }

    const data = {
      name_author: name,
    };

    try {
      if (editingAuthor) {
        await updateAuthor(editingAuthor.id_author, data);
      } else {
        await addAuthor(data);
      }
      setIsModalOpen(false);
      setEditingAuthor(null);
    } catch (err) {
      console.error('Error al enviar el formulario:', err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-[120px] lg:pb-0">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <AdminSidebar
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          userName="Administrador"
        />
      </div>
      {/* Mobile Bottom Bar */}
      <div className="lg:hidden">
        <AdminDownbar />
      </div>
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-3 border-b border-border bg-card">
          <div className="p-3">
            <div className="max-w-7xl mx-auto">
              {/* Encabezado con título y botón */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-[#c375461a]">
                      <Icon name="Feather" size={24} className="text-[#c37546]" />
                    </div>
                    <h1 className="text-2xl font-heading font-bold text-card-foreground">
                      Gestión de Autores
                    </h1>
                  </div>
                    <p className="text-sm italic text-muted-foreground mt-2">
                        “El espíritu de Jehová ha hablado por mí, y su palabra ha estado en mi lengua.” — 2 Samuel 23:2
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => {
                      setEditingAuthor(null);
                      setIsModalOpen(true);
                    }}
                    className="bg-primary text-white px-4 py-2 rounded flex items-center gap-2 shadow-sm hover:bg-primary/90 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    aria-label="Agregar nuevo autor"
                  >
                    <Icon name="Plus" size={16} />
                    <span className="font-medium">Nuevo autor</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Search Bar Above Table */}
            <div className="px-4 sm:px-6 lg:px-0 mb-6">
              <div className="relative w-full">
                <Input
                  type="search"
                  placeholder="Buscar autores..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 w-full"
                />
                <Icon 
                  name="Search" 
                  size={18} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
                />
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center space-x-3">
                  <Icon name="Loader2" size={24} className="animate-spin text-primary" />
                  <span className="text-muted-foreground">Cargando autores...</span>
                </div>
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-4 text-red-600">
                    {error}
                    <button onClick={clearError} className="ml-2 underline text-sm">Limpiar</button>
                  </div>
                )}

                <div className="w-full max-w-screen-lg mx-auto overflow-x-auto border border-gray-200 p-4 sm:p-6 rounded-lg bg-white shadow-sm">
                  <table
                    className="min-w-[320px] w-full text-[12px] sm:text-[14px] table-auto"
                    style={{ fontFamily: '"Poppins", Arial, sans-serif' }}
                  >
                    <thead className="bg-[#0c3454]">
                      <tr>
                        <th className="px-4 py-2 sm:px-10 sm:py-3 text-left text-white font-semibold text-[13px] sm:text-[15px] border-r border-gray-300">Nombre</th>
                        <th className="px-2 py-2 sm:px-4 sm:py-3 text-left text-white font-semibold text-[13px] sm:text-[15px]">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(filteredAuthors) && filteredAuthors.map((author, index) => (
                        <tr
                          key={author.id_author}
                          className="border-b border-gray-200 transition-colors duration-200 hover:bg-[#e8f4f8]"
                          style={{ backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#ffffff' }}
                        >
                          <td className="px-4 py-2 sm:px-10 sm:py-3 border-r border-gray-200 font-semibold text-[#2d3748]">
                            {author.name_author}
                          </td>
                          <td className="px-2 py-2 sm:px-4 sm:py-3 flex gap-2 items-center justify-start">
                            {/* Botón Editar */}
                            <button
                              onClick={() => { setEditingAuthor(author); setIsModalOpen(true); }}
                              className="p-2 sm:px-3 sm:py-2 text-white bg-[#c37546] hover:bg-[#a85d3a] rounded transition-colors duration-200 flex items-center justify-center sm:justify-start gap-0 sm:gap-1"
                              title="Editar"
                            >
                              <Icon name="Edit" size={16} />
                              <span className="hidden sm:inline text-sm font-medium">Editar</span>
                            </button>

                            {/* Botón Eliminar */}
                            <button
                              onClick={async () => {
                                try {
                                  await deleteAuthor(author.id_author, author.name_author);
                                } catch (error) {
                                  // Usuario canceló la eliminación
                                }
                              }}
                              className="p-2 sm:px-3 sm:py-2 bg-red-600 text-white hover:bg-red-700 rounded transition-colors duration-200 flex items-center justify-center sm:justify-start gap-0 sm:gap-1"
                              title="Eliminar"
                            >
                              <Icon name="Trash" size={16} />
                              <span className="hidden sm:inline text-sm font-medium">Eliminar</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" style={{fontFamily: 'Georgia, "Times New Roman", serif'}}>
            <h2 className="text-xl font-semibold mb-4">{editingAuthor ? 'Editar autor' : 'Nuevo autor'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4" style={{fontFamily: 'Georgia, "Times New Roman", serif'}}>
              <div className="p-4 rounded-lg" style={{backgroundColor: '#f8f9fa'}}>
                <h3 className="text-lg font-semibold mb-3" style={{color: '#0c3454'}}>Información del Autor</h3>
                <input
                  type="text"
                  name="name"
                  defaultValue={editingAuthor?.name_author || ''}
                  placeholder="Nombre del autor"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 transition-all"
                  style={{fontFamily: 'Georgia, "Times New Roman", serif'}}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#c37546';
                    e.target.style.boxShadow = 'none';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingAuthor(null);
                  }}
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-all flex items-center gap-2 shadow-sm"
                  style={{fontFamily: 'Georgia, \"Times New Roman\", serif'}}
                >
                  <Icon name="X" size={16} /> Cancelar
                </button>

                <button
                  type="submit"
                  className="px-6 py-3 text-white rounded-lg font-medium transition-all flex items-center gap-2 shadow-lg"
                  style={{backgroundColor: '#0c3454', fontFamily: 'Georgia, \"Times New Roman\", serif'}}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#c37546'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#0c3454'}
                >
                  <Icon name={editingAuthor ? 'Save' : 'Plus'} size={16} />
                  {editingAuthor ? 'Actualizar Autor' : 'Crear Autor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAuthorsScreen;
